import { Button, Flex, Modal, Select } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineClose, AiOutlineDownload } from "react-icons/ai";

import { useSandboxEndpoints } from "sandbox/api/hooks/useSandboxQueries";
import { useCredentials } from "sandbox/hooks/useCredentials";
import { getCredentialSecret } from "sandbox/utils/credentialStore";
import { downloadPostmanCollection } from "sandbox/utils/postmanExport";

import s from "./DownloadCollectionModal.module.css";

interface DownloadCollectionModalProps {
  onClose: () => void;
  open: boolean;
}

export const DownloadCollectionModal = ({
  open,
  onClose,
}: DownloadCollectionModalProps) => {
  const { t } = useTranslation(["sandbox", "common"]);
  const { credentials } = useCredentials();
  const { data: endpointGroups } = useSandboxEndpoints();

  const [selectedId, setSelectedId] = useState<string>();
  // Default to the first credential until the user picks another.
  const selected = credentials.find(c => c.id === selectedId) ?? credentials[0];

  const handleDownload = () => {
    downloadPostmanCollection(
      endpointGroups ?? [],
      selected
        ? {
            clientId: selected.clientId,
            clientSecret: getCredentialSecret(selected.id),
          }
        : undefined,
    );
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={380}
      centered
      destroyOnHidden
    >
      <Flex align="center" justify="space-between" className={s.header}>
        <span className={s.title}>
          {t("components.downloadCollectionModal.title")}
        </span>
        <Button
          type="text"
          icon={<AiOutlineClose />}
          onClick={onClose}
          aria-label={t("common:components.buttons.close")}
          className={s.closeButton}
        />
      </Flex>
      <p className={s.description}>
        {t("components.downloadCollectionModal.description")}
      </p>
      {credentials.length > 1 && (
        <Select
          data-testid="download-credential-select"
          aria-label={t("components.downloadCollectionModal.credentialLabel")}
          style={{ width: "100%", marginBottom: "var(--ant-margin-lg)" }}
          value={selected?.id}
          onChange={setSelectedId}
          options={credentials.map(c => ({
            value: c.id,
            label: `${c.name} (${c.clientId})`,
          }))}
        />
      )}
      <Button
        type="primary"
        block
        size="large"
        icon={<AiOutlineDownload />}
        onClick={handleDownload}
      >
        {t("components.downloadCollectionModal.download")}
      </Button>
    </Modal>
  );
};
